// MACHINERY V2 (Stroje) — capability badges (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9. Reference impl:
// design_handoff_machinery_v3/machinery-live.jsx (`CapabilityBadge(s)` +
// the AI / telemetry glyphs) + README.md §8. The MIDAS/LIVE/OEM dot cluster
// is a sibling module (ReadinessDots.tsx) because the popup and the profile
// card consume it too.
//
// "CAPABILITY BADGES ARE THE READINESS SURFACE" (locked): on fleet + catalog
// cards there is NO readiness text row — these two badges plus their hover
// tooltip carry the whole signal.
//
// ABSENCE = AN UNLIT BADGE, NEVER A HIDDEN ONE (locked). There is deliberately
// no `if (!readiness) return null` anywhere below: BOTH badges always render,
// unlit + red cross + an explaining tooltip. The adapter guarantees this by
// always returning midasLit / telemetry, so there is no "nothing to show"
// branch to take.
//
// THE BADGE IS THE DISCLOSURE TRIGGER (review fix 3). It opens
// ReadinessPopupV2 on click / tap / Enter / Space. That is NOT a CTA: it
// reveals detail, and the two commercial CTAs live only inside the popup.
// The affordance had to move here off the tooltip because Radix tooltips do
// not open on touch and close as focus leaves the trigger, which left the
// readiness surface unreachable for touch and keyboard users — see CapTip.tsx.
//
// CONNECTED = STATIC, NOTHING CLICKABLE (locked). Gated on the EXISTING
// `presentation.interactive` flag the adapter already derives — there is no
// second gate here, so this rule cannot drift from the other surfaces.
//
// THE HANDLER IS ALWAYS ATTACHED, INTERACTIVE OR NOT (M2.3 fix). The badge sits
// ON a fleet card / grouped row whose own onClick navigates to the profile.
// The first version attached the handler ONLY in the interactive branch
// (`onClick={open ? handler : undefined}`), so on a CONNECTED machine — where
// `interactive` is false by rule — the badge still rendered a focusable
// <button> with NO handler, and the click bubbled straight to the host and
// navigated. "Static" was implemented as "no popup"; it has to mean "inert".
// So: stopPropagation ALWAYS, and call open() only when interactive. The
// `interactive` flag remains the ONE gate on the popup — it never gates the
// swallow. Fixing it here rather than at the call sites covers FleetCard,
// FleetRow and every future consumer with one change.
//
// Nothing in this file re-derives readiness: it consumes
// MachineryV2ReadinessPresentation and never touches a raw readiness field.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import CapTip from "./CapTip";
import { MachineryV2ReadinessPresentation } from "./readinessPresentation";

export type CapabilityKind = "ai" | "telemetry";

const AiGlyph: React.FC<{ size: number }> = ({ size }) => (
  <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden>
    <path d="M6 0.6 L7 5 L11.4 6 L7 7 L6 11.4 L5 7 L0.6 6 L5 5 Z" fill="currentColor" />
  </svg>
);

const TelemetryGlyph: React.FC<{ size: number }> = ({ size }) => (
  <svg
    viewBox="0 0 12 12"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="6" cy="9" r="1.1" fill="currentColor" stroke="none" />
    <path d="M3.7 7.1 a3.2 3.2 0 0 1 4.6 0" />
    <path d="M2.1 5.4 a5.6 5.6 0 0 1 7.8 0" />
  </svg>
);

const Tick: React.FC = () => (
  <svg viewBox="0 0 10 10" width="7" height="7" aria-hidden>
    <path
      d="M2 5.2 L4.2 7.4 L8 3"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Cross: React.FC = () => (
  <svg viewBox="0 0 10 10" width="7" height="7" aria-hidden>
    <path
      d="M2.4 2.4 L7.6 7.6 M7.6 2.4 L2.4 7.6"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

/** The exact translate fn useTranslation hands back — typing this as a plain
 *  (key: string) => string rejects i18next's overloaded TFunction. */
type Translate = ReturnType<typeof useTranslation>["translate"];

interface TipCopy {
  title: string;
  line: string;
  hint: string | null;
}

/** Tooltip copy for one capability, built ENTIRELY from the presentation.
 *  Every branch below switches on an adapter field (aiProvenance, telemetry,
 *  oemLit, summary.docPart) — never on a raw readiness field. */
const tipCopy = (
  kind: CapabilityKind,
  p: MachineryV2ReadinessPresentation,
  translate: Translate
): TipCopy => {
  const k = (name: string) => "myMachinery:v2.readiness." + name;
  if (kind === "ai") {
    if (p.aiProvenance === "none") {
      return {
        title: translate(k("capAiTitle")),
        line: translate(k("capAiNoDocs")),
        hint: translate(k("capAiNoDocsHint")),
      };
    }
    // The doc phrase comes from the util's own summary parts ("3 docs" /
    // "1 dok."), so the tooltip can never disagree with the card row.
    const docs = p.summary.docPart || "";
    const hint =
      p.aiProvenance === "oem"
        ? "★ " + translate(k("capAiProvOem"))
        : p.aiProvenance === "direct"
        ? translate(k("capAiProvDirect"))
        : translate(k("capAiProvFanout"));
    return {
      title: translate(k("capAiTitle")),
      line: translate(k("capAiDocs"), { docs }),
      hint,
    };
  }

  if (p.telemetry === "connected") {
    return {
      title: translate(k("capTelemetryTitle")),
      line: translate(k(p.oemLit ? "capTelConnectedOem" : "capTelConnectedEdge")),
      hint: translate(k("capTelConnectedHint")),
    };
  }
  if (p.telemetry === "possible") {
    return {
      title: translate(k("capTelemetryTitle")),
      line: translate(k("capTelPossible")),
      // Markers stay language-neutral and live in the composition, never in
      // the locale string (the midasReadiness.ts convention).
      hint: p.oemLit
        ? "★ " + translate(k("capTelPossibleHintOem"))
        : translate(k("capTelPossibleHintEdge")),
    };
  }
  return {
    title: translate(k("capTelemetryTitle")),
    line: translate(k("capTelNone")),
    hint: translate(k("capTelNoneHint")),
  };
};

export interface CapabilityBadgeProps {
  kind: CapabilityKind;
  presentation: MachineryV2ReadinessPresentation;
  size?: number;
  /** Opens ReadinessPopupV2. Ignored while `presentation.interactive` is
   *  false, i.e. a connected machine's badge stays inert. */
  onOpenDetails?: (() => void) | null;
}

export const CapabilityBadge: React.FC<CapabilityBadgeProps> = ({
  kind,
  presentation: p,
  size = 22,
  onOpenDetails = null,
}) => {
  const { translate } = useTranslation("myMachinery");
  // "on" = the capability is ACTUALLY in place: any ingested doc lights AI
  // (midasDotLit), a real gateway link lights telemetry. "Possible" telemetry
  // stays UNLIT with an explaining tooltip — the tri-state lives in the dot
  // cluster (liveVisual), not in this binary tick/cross badge.
  const on = kind === "ai" ? p.midasLit : p.telemetry === "connected";
  const copy = tipCopy(kind, p, translate);
  // ONE gate: the adapter's `interactive`. No second condition lives here.
  const open = p.interactive ? onOpenDetails : null;

  // THE HANDLER IS ALWAYS ATTACHED — see the "static badge navigated" note in
  // the file header. `open` still decides whether the popup opens; it does NOT
  // decide whether the click is swallowed.
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) open();
  };

  return (
    <CapTip title={copy.title} line={copy.line} hint={copy.hint}>
      <button
        type="button"
        aria-label={copy.title + ": " + copy.line}
        aria-haspopup={open ? "dialog" : undefined}
        onClick={onClick}
        style={{
          position: "relative",
          width: size,
          height: size,
          padding: 0,
          borderRadius: 6,
          background: on ? "var(--mod-accent-soft)" : "var(--card-2)",
          border: "1px solid " + (on ? "var(--mod-accent-line)" : "var(--border)"),
          color: on ? "var(--mod-accent)" : "var(--faint)",
          display: "inline-grid",
          placeItems: "center",
          cursor: open ? "pointer" : "help",
          flexShrink: 0,
        }}
      >
        {kind === "ai" ? (
          <AiGlyph size={Math.round(size * 0.55)} />
        ) : (
          <TelemetryGlyph size={Math.round(size * 0.55)} />
        )}
        <span
          style={{
            position: "absolute",
            bottom: -3,
            right: -3,
            width: 11,
            height: 11,
            borderRadius: 999,
            background: on ? "var(--green)" : "var(--red)",
            color: "#fff",
            display: "inline-grid",
            placeItems: "center",
            border: "1.5px solid var(--card)",
          }}
        >
          {on ? <Tick /> : <Cross />}
        </span>
      </button>
    </CapTip>
  );
};

export interface CapabilityBadgesProps {
  presentation: MachineryV2ReadinessPresentation;
  size?: number;
  onOpenDetails?: (() => void) | null;
}

/** The pair, in locked order: AI then telemetry. Both ALWAYS render. */
export const CapabilityBadges: React.FC<CapabilityBadgesProps> = ({
  presentation,
  size = 22,
  onOpenDetails = null,
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <CapabilityBadge
      kind="ai"
      presentation={presentation}
      size={size}
      onOpenDetails={onOpenDetails}
    />
    <CapabilityBadge
      kind="telemetry"
      presentation={presentation}
      size={size}
      onOpenDetails={onOpenDetails}
    />
  </span>
);

export default CapabilityBadge;
